import { marginBp, pence, vatOn, type Pence } from './money.ts'
import type { Frequency, SettingsValue } from './settings.ts'

/**
 * The deterministic quote engine (ADR-0004). The server is the only authority
 * for totals; the browser uses the same code for instant previews.
 */

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type Weekday = (typeof WEEKDAYS)[number]
const DAY_NAMES: Record<Weekday, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
}
export const DAY_SHORT: Record<Weekday, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
}

export interface QuoteLine {
  id: string
  priceItemId: string | null
  description: string
  /** 'days': quantity is the number of chosen weekdays. 'quantity': free quantity. */
  basis: 'days' | 'quantity'
  days: Weekday[]
  quantity: number
  unitName: string
  unitPricePence: number
  /** Internal unit cost, snapshotted from the price book. Never shown to clients. */
  costPence: number
  chargeType: 'one_off' | 'recurring'
  frequency: Frequency | null
  vatRateBp: number
  vatCode: string
}

export interface CalculatedLine extends QuoteLine {
  netPence: number
  vatPence: number
  grossPence: number
}

export type PeriodKey = Frequency | 'one_off'
export interface PeriodTotal {
  key: PeriodKey
  label: string
  netPence: number
  vatPence: number
  grossPence: number
}

export interface QuoteTotals {
  lines: CalculatedLine[]
  periods: PeriodTotal[]
  combined: { netPence: number; vatPence: number; grossPence: number }
  internal: { costPence: number; netPence: number; marginBp: number | null }
  annualValuePence: number
}

const PERIOD_ORDER: PeriodKey[] = [
  'weekly',
  'fortnightly',
  'monthly',
  'quarterly',
  'annually',
  'one_off',
]
const PERIOD_LABEL: Record<PeriodKey, string> = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  one_off: 'One-off',
}
const PER_YEAR: Record<PeriodKey, number> = {
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  annually: 1,
  one_off: 1,
}

/** Quantity in hundredths, exact for up to two decimal places. */
const hundredths = (q: number) => Math.round(q * 100)

export const periodOf = (l: Pick<QuoteLine, 'chargeType' | 'frequency'>): PeriodKey =>
  l.chargeType === 'one_off' || !l.frequency ? 'one_off' : l.frequency

export function lineQuantity(l: Pick<QuoteLine, 'basis' | 'days' | 'quantity'>): number {
  return l.basis === 'days' ? l.days.length : l.quantity
}

export function calculateQuote(
  input: QuoteLine[],
  opts: { vatRegistered: boolean; rounding: 'per_line' | 'per_total' },
): QuoteTotals {
  const lines: CalculatedLine[] = input.map((l) => {
    const quantity = lineQuantity(l)
    const net = pence(Math.floor((hundredths(quantity) * l.unitPricePence + 50) / 100))
    const vat = opts.vatRegistered ? vatOn(net, l.vatRateBp) : pence(0)
    return { ...l, quantity, netPence: net, vatPence: vat, grossPence: net + vat }
  })

  const periods: PeriodTotal[] = []
  for (const key of PERIOD_ORDER) {
    const group = lines.filter((l) => periodOf(l) === key)
    if (!group.length) continue
    const netPence = group.reduce((s, l) => s + l.netPence, 0)
    let vatPence: number
    if (!opts.vatRegistered) vatPence = 0
    else if (opts.rounding === 'per_line') vatPence = group.reduce((s, l) => s + l.vatPence, 0)
    else {
      const byRate = new Map<number, number>()
      for (const l of group) byRate.set(l.vatRateBp, (byRate.get(l.vatRateBp) ?? 0) + l.netPence)
      vatPence = [...byRate].reduce((s, [bp, net]) => s + vatOn(pence(net), bp), 0)
    }
    periods.push({
      key,
      label: PERIOD_LABEL[key],
      netPence,
      vatPence,
      grossPence: netPence + vatPence,
    })
  }

  const sum = (k: 'netPence' | 'vatPence' | 'grossPence') => periods.reduce((s, p) => s + p[k], 0)
  const costPence = lines.reduce(
    (s, l) => s + Math.floor((hundredths(l.quantity) * l.costPence + 50) / 100),
    0,
  )
  const netPence = sum('netPence')
  return {
    lines,
    periods,
    combined: { netPence, vatPence: sum('vatPence'), grossPence: sum('grossPence') },
    internal: { costPence, netPence, marginBp: marginBp(pence(netPence), pence(costPence)) },
    annualValuePence: periods.reduce((s, p) => s + p.netPence * PER_YEAR[p.key], 0),
  }
}

/** "Monday to Friday", "Monday, Wednesday and Friday", "Every day". */
export function daysLabel(days: Weekday[]): string {
  const sorted = WEEKDAYS.filter((d) => days.includes(d))
  if (sorted.length === 7) return 'Every day'
  if (sorted.length === 0) return ''
  const idx = sorted.map((d) => WEEKDAYS.indexOf(d))
  const consecutive = idx.every((v, i) => i === 0 || v === idx[i - 1]! + 1)
  if (consecutive && sorted.length >= 3)
    return `${DAY_NAMES[sorted[0]!]} to ${DAY_NAMES[sorted.at(-1)!]}`
  const names = sorted.map((d) => DAY_NAMES[d])
  return names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`
}

// ---- approvals ------------------------------------------------------------
type Approvals = Omit<SettingsValue<'approvals'>, 'approverRoles'> & {
  approverRoles: readonly string[]
}

export function needsApproval(
  settings: Approvals,
  q: { annualValuePence: number; marginBp: number | null; discountBp: number },
  actor: 'staff' | 'agent',
): boolean {
  if (actor === 'agent') return true // Hermes never sends without a person (Richard, 2026-09-25)
  if (settings.mode === 'always') return true
  if (settings.mode === 'never') return false
  if (settings.valueAbovePence > 0 && q.annualValuePence > settings.valueAbovePence) return true
  if (settings.marginBelowBp > 0 && q.marginBp !== null && q.marginBp < settings.marginBelowBp)
    return true
  if (settings.discountAboveBp > 0 && q.discountBp > settings.discountAboveBp) return true
  return false
}

export function canApprove(
  settings: Approvals,
  approver: { id: string; roles: string[] },
  authorId: string,
): boolean {
  if (!approver.roles.some((r) => settings.approverRoles.includes(r))) return false
  if (approver.id === authorId && !settings.allowSelfApproval) return false
  return true
}

// ---- lifecycle ------------------------------------------------------------
export const QUOTE_STATUSES = [
  'draft',
  'in_review',
  'approved',
  'sent',
  'viewed',
  'accepted',
  'declined',
  'withdrawn',
  'expired',
] as const
export type QuoteStatus = (typeof QUOTE_STATUSES)[number]
export type QuoteAction =
  | 'submit'
  | 'approve'
  | 'reject'
  | 'send'
  | 'view'
  | 'accept'
  | 'decline'
  | 'withdraw'
  | 'revise'
  | 'expire'

const TRANSITIONS: Record<QuoteStatus, Partial<Record<QuoteAction, QuoteStatus>>> = {
  draft: { submit: 'in_review', send: 'sent' },
  // Sending straight from review is allowed only when approval is not required (checked by the API).
  in_review: { approve: 'approved', reject: 'draft', revise: 'draft', send: 'sent' },
  approved: { send: 'sent', revise: 'draft' },
  sent: {
    view: 'viewed',
    accept: 'accepted',
    decline: 'declined',
    withdraw: 'withdrawn',
    revise: 'draft',
    expire: 'expired',
  },
  viewed: {
    view: 'viewed',
    accept: 'accepted',
    decline: 'declined',
    withdraw: 'withdrawn',
    revise: 'draft',
    expire: 'expired',
  },
  accepted: {},
  declined: { revise: 'draft' },
  withdrawn: { revise: 'draft' },
  expired: { revise: 'draft' },
}

/** The resulting status, or null when the action is not allowed from `from`. */
export function nextStatus(from: QuoteStatus, action: QuoteAction): QuoteStatus | null {
  return TRANSITIONS[from][action] ?? null
}

export const STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: 'Draft',
  in_review: 'Awaiting approval',
  approved: 'Approved',
  sent: 'Sent',
  viewed: 'Viewed',
  accepted: 'Accepted',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
}

/** Stable JSON (sorted keys) for hashing snapshots. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  if (value && typeof value === 'object') {
    return `{${Object.keys(value)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonicalJson((value as Record<string, unknown>)[k])}`)
      .join(',')}}`
  }
  return JSON.stringify(value)
}

export type { Pence }
